/**
 * @module components/canvas/CanvasView
 * @summary The drawing surface: <canvas> + custom scrollbars + minimap +
 *   inline label input. This DOM is OWNED by the engine (queried by id); React
 *   renders it once and never re-renders it, so the RAF loop is undisturbed.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 488-562.
 */
import { useRef } from 'react';
import { useCanvasEngine } from '../../hooks/useCanvasEngine';
import { engine } from '../../engine/CanvasEngine';
import { Minimap } from './Minimap';

/** MIME-ish key used for library drag-and-drop onto the canvas. */
export const SHAPE_DND_TYPE = 'application/x-pfd-shape';

export function CanvasView() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  useCanvasEngine(canvasRef, wrapRef);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const type = e.dataTransfer.getData(SHAPE_DND_TYPE);
    if (type) engine.addShapeAtClient(type, e.clientX, e.clientY);
  };
  const onDragOver = (e: React.DragEvent) => {
    if (e.dataTransfer.types.includes(SHAPE_DND_TYPE)) e.preventDefault();
  };

  return (
    <div id="canvas-area">
      <div id="canvas-row">
        <div id="canvas-wrap" ref={wrapRef} onDrop={onDrop} onDragOver={onDragOver}>
          <canvas id="main-canvas" ref={canvasRef} />
          {/* Inline label editor (engine positions + shows/hides it). */}
          <input id="label-input" type="text" />
          {/* Multi-line editor for Zone Boxes (paragraphs + bullet points). */}
          <textarea id="zone-input" aria-label="Zone box text" placeholder="Write text…" />
          <Minimap />
        </div>
        <div id="v-scrollbar">
          <div id="v-scrollbar-thumb" />
        </div>
      </div>
      <div id="h-scrollbar-row">
        <div id="h-scrollbar">
          <div id="h-scrollbar-thumb" />
        </div>
        <div id="scroll-corner" />
      </div>
    </div>
  );
}
